import { toast } from 'sonner'
import { toggleLoader } from '@/hooks/useDashboardLoader.jsx'
import { handleError } from '@/services/errorHandler.js'
import { useParams } from 'react-router-dom'
import { LINKEDIN_CLIENT_ID } from '@/config-global.jsx'
import { useCurrentSpace } from '@/hooks/useCurrentSpace.jsx'
import { useAuthContext } from '@/context-state/AuthContext.jsx'
import { useSyncEntitlements } from '@/hooks/useSyncEntitlements'
import { useRelevantIntegration } from '@/hooks/useRelevantIntegration.jsx'
import { IntegrationProviders } from '@/consts/index.js'
import * as API from '@/services/api.js'

export const useLinkedInConnection = () => {
    const { accountId } = useParams()
    const { updateSpaceState } = useAuthContext()
    const { space } = useCurrentSpace()
    const syncEntitlements = useSyncEntitlements()
    const { integration: linkedinIntegration } = useRelevantIntegration(IntegrationProviders.LINKEDIN_ADS)

    const connectToLinkedIn = async () => {
        try {
            toggleLoader(true)

            // state carries the Spring accountId so the backend knows which account to attach the integration to.
            const state = accountId
            const redirectUri = encodeURIComponent(`${window.location.origin}/connect/linkedin`)
            // Full read-scope set available under our Advertising API product. Requesting them all
            // upfront so the partner doesn't have to re-auth as we add more features.
            //   r_ads                  — list ad accounts the member administers
            //   r_ads_reporting        — read campaign/creative analytics
            //   r_organization_social  — read posts (needed to hydrate ad creative content)
            //   r_organization_admin   — read org page details + analytics
            //   r_basicprofile         — basic user profile
            //   r_1st_connections_size — connections count (audience-size context)
            const scope = encodeURIComponent('r_ads r_ads_reporting r_organization_social r_organization_admin r_basicprofile r_1st_connections_size')

            const linkedinAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${LINKEDIN_CLIENT_ID}&state=${state}&scope=${scope}&redirect_uri=${redirectUri}`

            window.location.replace(linkedinAuthUrl)
        } catch (e) {
            console.error(e)
            toast.error(e.message)
            handleError(e)
            toggleLoader(false)
        }
    }

    const handleLinkedInAdAccountSelection = async (selectedAccountIds) => {
        try {
            toggleLoader(true)

            if (linkedinIntegration?.adAccounts) {
                const selectedAdAccounts = linkedinIntegration.adAccounts
                    .filter(account => selectedAccountIds.includes(account.id))
                    .map(account => ({
                        ...account,
                        provider: linkedinIntegration?.provider || 'LINKEDIN_ADS'
                    }))

                const normalizeProvider = (provider) => String(provider || '')
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_+|_+$/g, '')

                const existingNonLinkedInAccounts = space?.adAccounts?.filter(account => normalizeProvider(account.provider) !== 'linkedin_ads') || []
                const nextAdAccounts = [...existingNonLinkedInAccounts, ...selectedAdAccounts]
                await API.updateSpace(space._id, { adAccounts: nextAdAccounts })

                const updatedSpace = { ...space, adAccounts: nextAdAccounts }
                updateSpaceState(space._id, updatedSpace)

                syncEntitlements()
                toast.success('Ad Accounts updated successfully')
            }
        } catch (e) {
            console.error(e)
            toast.error('An error occurred while updating ad accounts')
        } finally {
            toggleLoader(false)
        }
    }

    return { connectToLinkedIn, handleLinkedInAdAccountSelection }
}
